/**
 * One app for everyone: who is looking, which groups they get, and which pages they may open.
 * The screens are the same for everyone; this only decides what shows. The server sends only
 * what each account may see, so hiding a page here is about tidiness, not secrecy.
 *
 *   owner                 every group and page, plus Money and Accounts
 *   club treasurer        the full app for their club, pages per "Who sees what"
 *   member                their groups, pages per "Who sees what"
 *   owner in Member view  exactly what a member sees (for themselves or someone they pick)
 */
import { MEMBER_ACCESS_DEFAULTS, treasurerAccessFor } from "@split/core";
import { api } from "./api.ts";
import { member } from "./member.svelte.ts";
import { router, type GroupPage } from "./router.svelte.ts";
import { app, type GroupInfo } from "./store.svelte.ts";

/** The access key behind each group page (pages not listed are owner-only, except Ledger). */
const KEY: Partial<Record<GroupPage, "people" | "receipts" | "insights" | "history" | "settings">> =
  {
    people: "people",
    payments: "people",
    trips: "receipts",
    insights: "insights",
    history: "history",
    settings: "settings",
  };

class Nav {
  /** The owner looking through Member view. */
  preview = $derived(app.role === "owner" && app.viewAsMember);
  /** Everything below is shown the way a member sees it. */
  asMember = $derived(app.signedIn !== false && (app.role === "member" || this.preview));
  isOwner = $derived(app.role === "owner" && !this.asMember);

  /** The groups in the sidebar: every group for the owner; your own groups otherwise. */
  groups = $derived<GroupInfo[]>(
    this.asMember ? (member.groups ?? []).map((g) => ({ id: g.id, name: g.name })) : app.groups,
  );

  private routeGid = $derived(router.route.name === "group" ? router.route.groupId : null);

  /** This account runs the open club as its treasurer (the full app for that one group). */
  clubTreasurer = $derived(
    this.asMember &&
      !this.preview &&
      !!this.routeGid &&
      member.groups?.find((g) => g.id === this.routeGid)?.role === "treasurer",
  );

  /** The group whose data the screens hold right now. */
  openGid = $derived(
    !this.asMember || this.clubTreasurer
      ? app.memberSession
        ? null
        : app.groupId
      : (app.memberSession?.groupId ?? null),
  );

  /** Whether this account has a Tutoring space (as a family or a tutor). */
  hasTutoring = $state(false);

  /** Whether the open group's page shows for whoever is looking. */
  canSee(page: GroupPage): boolean {
    if (!this.asMember || page === "ledger") return true;
    if (page === "more") return true;
    const key = KEY[page];
    if (!key) return false; // Ask: the owner's assistant
    if (this.clubTreasurer) {
      const me = member.groups?.find((g) => g.id === this.routeGid)?.personId ?? null;
      return treasurerAccessFor(app.current.settings, me)[key];
    }
    if (key === "history" || key === "settings") return false;
    return { ...MEMBER_ACCESS_DEFAULTS, ...(app.memberSession?.access ?? {}) }[key];
  }

  /** Spaces for whoever is looking: Splits always, Tutoring when you're in it, Money for you. */
  spaces = $derived<("splits" | "tutoring" | "money")[]>(
    this.isOwner
      ? ["splits", "tutoring", "money"]
      : this.hasTutoring && !this.preview
        ? ["splits", "tutoring"]
        : ["splits"],
  );

  async checkTutoring() {
    if (!this.asMember || this.preview) return;
    try {
      const r = await api<{ students: unknown[]; tutors: unknown[] }>("/api/me/tutoring");
      this.hasTutoring = r.students.length > 0 || r.tutors.length > 0;
    } catch {
      /* offline: keep what we had */
    }
  }
}

export const nav = new Nav();
